import styled from "@emotion/styled";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CheckboxFilter,
  formatBalance,
  getFirstWord,
  greenTag,
  imageFromPlatformName,
  makeSimpleFilters,
  redTag,
  StepTable,
  TagInfo,
  TokenTag,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useLanguageContext } from "../../../contexts/language/language";
import type { MarginPerpetualPosition } from "../../../types/margin";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { ExpiryPopover, PAGE_SIZE_OPTIONS, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

interface MarginPerpetualPositionsProps {
  positions: MarginPerpetualPosition[];
}

export const MarginPerpetualPositions: FC<MarginPerpetualPositionsProps> = ({ positions }) => {
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "marginPerpetualPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: {
            marginTrading: { marginPerpPositions: marginPerpText },
          },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(positions.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [positions.length, paginationSetting]);

  const totalPerpetualsValue = useMemo(() => {
    return positions.reduce((total, currentValue) => {
      const unrealizedPnl = currentValue.unrealizedPnl || 0;
      return total + unrealizedPnl;
    }, 0);
  }, [positions]);

  const perpetualPositionsColumns: StepColumnsType<MarginPerpetualPosition> = [
    {
      title: marginPerpText.perpetual,
      key: "market",
      dataIndex: "market",
      filters: makeSimpleFilters(positions.map((position) => position.market ?? "")),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => (record.market ?? "").startsWith(value as string),
      filteredValue: filteredInfo.market ?? null,
      render(_, record) {
        return (
          <AssetContainer>
            <TokenContainer>
              <TokenIconText bold withoutIcon>
                {record.market}
              </TokenIconText>
              {record.expiry && <ExpiryPopover expiryTimeStamp={record.expiry} />}
            </TokenContainer>
            <TokenTag
              icon={<URLIcon size={16} src={imageFromPlatformName(record.kind) ?? ""} />}
              text={getFirstWord(record.kind) ?? ""}
              uppercase={false}
            />
          </AssetContainer>
        );
      },
    },
    {
      title: common.side,
      key: "side",
      dataIndex: "side",
      className: "column-center",
      width: 110,
      filters: [
        {
          text: "Long",
          value: "long",
        },
        {
          text: "Short",
          value: "short",
        },
      ],
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.side.startsWith(value as "long" | "short"),
      filteredValue: filteredInfo.side ?? null,
      render(side: string) {
        const tagColor = side === "long" ? greenTag : redTag;
        return (
          <div>
            <TagInfo useSpaceMono={false} tagType="text" tagColorOverride={tagColor}>
              {side === "long" ? marginPerpText.positionSides.long : marginPerpText.positionSides.short}
            </TagInfo>
          </div>
        );
      },
    },
    {
      title: marginPerpText.positionSize,
      key: "positionSize",
      dataIndex: "positionSize",
      className: "column-number",
      sorter: (a, b) => {
        const aPositionSize = a.positionSize ? a.positionSize : 0;
        const bPositionSize = b.positionSize ? b.positionSize : 0;
        return aPositionSize - bPositionSize;
      },
      render(positionSize: number, { baseDecimals }) {
        return <CellValue cellType="number">{formatBalance(positionSize, baseDecimals)}</CellValue>;
      },
    },
    {
      title: marginPerpText.notionalSize,
      key: "notionalSize",
      dataIndex: "notionalSize",
      className: "column-number",
      sorter: (a, b) => {
        const aNotionalSize = a.notionalSize ? a.notionalSize : 0;
        const bNotionalSize = b.notionalSize ? b.notionalSize : 0;
        return aNotionalSize - bNotionalSize;
      },
      render(notionalSize: number) {
        return <CellValue cellType="value">{notionalSize}</CellValue>;
      },
    },
    {
      title: marginPerpText.avgEntryPrice,
      key: "avgEntryPrice",
      dataIndex: "avgEntryPrice",
      className: "column-number",
      sorter: (a, b) => {
        const aAvgEntryPrice = a.avgEntryPrice ? a.avgEntryPrice : 0;
        const bAvgEntryPrice = b.avgEntryPrice ? b.avgEntryPrice : 0;
        return aAvgEntryPrice - bAvgEntryPrice;
      },
      render(avgEntryPrice: number) {
        return <CellValue cellType="value">{avgEntryPrice}</CellValue>;
      },
    },
    {
      title: marginPerpText.breakEvenPrice,
      key: "breakEvenPrice",
      dataIndex: "breakEvenPrice",
      className: "column-number",
      sorter: (a, b) => {
        const aBreakEvenPrice = a.breakEvenPrice ? a.breakEvenPrice : 0;
        const bBreakEvenPrice = b.breakEvenPrice ? b.breakEvenPrice : 0;
        return aBreakEvenPrice - bBreakEvenPrice;
      },
      render(breakEvenPrice: number) {
        return <CellValue cellType="value">{breakEvenPrice}</CellValue>;
      },
    },
    {
      title: marginPerpText.profitAndLoss,
      key: "unrealizedPnl",
      dataIndex: "unrealizedPnl",
      className: "column-number",
      defaultSortOrder: "descend",
      sorter: (a, b) => {
        const aUnrealizedPnl = a.unrealizedPnl ? a.unrealizedPnl : 0;
        const bUnrealizedPnl = b.unrealizedPnl ? b.unrealizedPnl : 0;
        return aUnrealizedPnl - bUnrealizedPnl;
      },
      render(unrealizedPnl: number) {
        return (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {unrealizedPnl}
          </CellValue>
        );
      },
    },
  ];
  return (
    <PerpPositionContainer>
      <ChevronCollapse title={marginPerpText.title} value={totalPerpetualsValue} isCollapseChild>
        <StepTable
          rowKey={({ kind, market, side }, i) => kind + market + side + i}
          columns={perpetualPositionsColumns}
          key="margin-perps-table"
          dataSource={positions}
          pagination={pagination}
          onChange={(pagination, filters) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
        />
      </ChevronCollapse>
    </PerpPositionContainer>
  );
};

const AssetContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TokenContainer = styled.div`
  display: flex;
  margin-right: 16px;
`;

const PerpPositionContainer = styled.div`
  margin-top: 20px;
`;
